<!-- src/components/ViewBeneficiaryCommitmentModal.vue -->
<template>
  <div
    ref="modalEl"
    class="modal fade"
    id="viewBeneficiaryCommitmentModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Beneficiary Commitment
          </h5>

          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>

        <div class="modal-body">
          <div
            v-if="loading"
            class="text-center text-muted py-4"
          >
            <i class="bi bi-hourglass-split me-2"></i>
            Loading…
          </div>

          <div
            v-else-if="error"
            class="alert alert-danger py-2 mb-0"
          >
            {{ error }}
          </div>

          <div v-else>
            <div class="mb-3">
              <div class="text-muted small">
                Beneficiary
              </div>

              <div class="fw-semibold">
                {{ beneficiary?.name || "—" }}
              </div>

              <div class="text-muted small">
                Guardian: {{ guardianName }}
              </div>
            </div>

            <div
              v-if="commitment"
              class="row g-3"
            >
              <div class="col-6">
                <div class="text-muted small">
                  Amount / month
                </div>

                <div class="fw-bold">
                  {{ formatMoney(commitment.amount) }}
                </div>
              </div>

              <div class="col-6">
                <div class="text-muted small">
                  Status
                </div>

                <span
                  class="badge"
                  :class="
                    commitment.status === 'active'
                      ? 'text-bg-success'
                      : 'text-bg-secondary'
                  "
                >
                  {{ commitment.status }}
                </span>
              </div>

              <div class="col-12">
                <div class="text-muted small">
                  Cycle
                </div>

                <div class="fw-semibold">
                  {{
                    commitment.cycle_start_period ||
                    "—"
                  }}
                  →
                  {{
                    commitment.cycle_end_period ||
                    "—"
                  }}
                </div>
              </div>

              <div class="col-6">
                <div class="text-muted small">
                  Cycle months
                </div>

                <div class="fw-semibold">
                  {{
                    commitment.cycle_months ?? "—"
                  }}
                </div>
              </div>

              <div class="col-6">
                <div class="text-muted small">
                  Activated at
                </div>

                <div class="fw-semibold">
                  {{
                    formatDateTime(
                      commitment.activated_at
                    )
                  }}
                </div>
              </div>
            </div>

            <div
              v-else
              class="alert alert-warning py-2 mb-0"
            >
              Commitment not found for the selected
              period.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";

export default {
  name: "ViewBeneficiaryCommitmentModal",

  data() {
    return {
      modal: null,
      loading: false,
      error: "",
      beneficiary: null,
      period: null,
      financialYearRuleId: null,
      commitment: null,
    };
  },

  computed: {
    guardianName() {
      return (
        this.beneficiary?.guardian_name || "—"
      );
    },

    guardianUserId() {
      return Number(
        this.beneficiary?.user_id ||
          this.beneficiary?.guardian_user_id ||
          0
      );
    },

    beneficiaryId() {
      return Number(
        this.beneficiary?.beneficiary_id ||
          this.beneficiary?.id ||
          0
      );
    },
  },

  methods: {
    formatMoney(value) {
      const amount = Number(value || 0);

      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatDateTime(value) {
      if (!value) {
        return "—";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },

    async open(
      beneficiary,
      period,
      financialYearRuleId = null
    ) {
      this.beneficiary = beneficiary
        ? {
            ...beneficiary,

            user_id: Number(
              beneficiary.user_id ||
                beneficiary.guardian_user_id ||
                0
            ),

            guardian_user_id: Number(
              beneficiary.guardian_user_id ||
                beneficiary.user_id ||
                0
            ),

            beneficiary_id: Number(
              beneficiary.beneficiary_id ||
                beneficiary.id ||
                0
            ),
          }
        : null;

      this.period = period || null;

      this.financialYearRuleId =
        financialYearRuleId
          ? Number(financialYearRuleId)
          : null;

      this.loading = true;
      this.error = "";
      this.commitment = null;

      if (!this.modal) {
        this.error = "Modal not initialized.";
        this.loading = false;
        return;
      }

      this.modal.show();

      try {
        if (!this.beneficiaryId) {
          throw new Error(
            "Beneficiary not provided."
          );
        }

        if (!this.guardianUserId) {
          throw new Error(
            "Guardian user was not provided."
          );
        }

        if (!this.period) {
          throw new Error(
            "Period not provided."
          );
        }

        const params = {
          user_id: this.guardianUserId,
          beneficiary_id:
            this.beneficiaryId,
          period: this.period,
        };

        if (this.financialYearRuleId) {
          params.financial_year_rule_id =
            this.financialYearRuleId;
        }

        const response = await http.get(
          "/commitments/active",
          {
            params,
          }
        );

        this.commitment =
          response?.data?.data || null;

        if (!this.commitment) {
          this.error =
            "No active commitment found for this period.";
        }
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load commitment.";
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.loading = false;
      this.error = "";
      this.beneficiary = null;
      this.period = null;
      this.financialYearRuleId = null;
      this.commitment = null;
    },
  },

  mounted() {
    const element = this.$refs.modalEl;

    if (!element) {
      return;
    }

    this.modal =
      Modal.getOrCreateInstance(element);

    element.addEventListener(
      "hidden.bs.modal",
      this.reset
    );
  },

  beforeUnmount() {
    const element = this.$refs.modalEl;

    if (element) {
      element.removeEventListener(
        "hidden.bs.modal",
        this.reset
      );
    }

    this.modal?.dispose();
    this.modal = null;
  },
};
</script>

<style scoped>
@media (max-width: 576px) {
  :deep(.modal-dialog) {
    margin: 0.75rem;
  }
}
</style>